import type {ReactElement} from 'react';
import {useRef, useState} from 'react';
import {reloadGrid} from '@Components/base-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {LazyLoadingGetterParams, LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import StockPanelError from '@Panels/stock-panel/components/stock-panel-error';
import {getGridItemForStockFavorite, getStockFavoritesPage, takeStockFavoritesProbePage} from '@Panels/stock-panel/stock-favorites.library';
import {FAVORITES_PAGE_SIZE} from '@Panels/stock-panel/stock-favorites.types';
import {STOCK_GRID_ITEM_DIMENSION} from '@Panels/stock-panel/stock-panel.types';
import {useShownTiles} from '@Panels/stock-panel/use-shown-tiles';
import {useAppDispatch} from '@/hooks';
import type {StockFavoritesGridProps} from './stock-favorites-grid.types';

/**
 * The user's saved items, newest first. The backend pages by row id, so the cursor lives here rather than in the
 * grid's additive offsets, and a buffer bridges the grid's "one more than the batch" probe to the server's pages.
 */
export function StockFavoritesGrid({...props}: StockFavoritesGridProps): ReactElement {
  const dispatch = useAppDispatch();
  const cursor = useRef<number | null>(null);
  const buffer = useRef<GridItemStorage[]>([]);
  const serverHasMore = useRef(true);
  const hasServedItems = useRef(false);
  const {dropTilesAlreadyShown, forgetShownTiles} = useShownTiles();
  const [isEmpty, setIsEmpty] = useState(false);
  const [hasError, setHasError] = useState(false);

  const resetPaging = (): void => {
    cursor.current = null;
    buffer.current = [];
    serverHasMore.current = true;
    hasServedItems.current = false;
    forgetShownTiles();
  };

  /**
   * Fills the buffer up to the probe size, one page after another (each page starts at the cursor the previous
   * one returned), until the server has no more. The query carries every limit of this view and its host, so the
   * server filters before it pages and every favorite it returns is shown.
   */
  const fillBuffer = async (probeSize: number): Promise<void> => {
    if (buffer.current.length >= probeSize || !serverHasMore.current) {
      return;
    }
    const page = await getStockFavoritesPage({...props.query, limit: FAVORITES_PAGE_SIZE, beforeId: cursor.current});
    cursor.current = page.nextCursor;
    serverHasMore.current = page.hasMore;
    buffer.current = [...buffer.current, ...page.items.map(getGridItemForStockFavorite)];
    await fillBuffer(probeSize);
  };

  const getItems = async (params: LazyLoadingGetterParams): Promise<LazyLoadingResponse> => {
    try {
      await fillBuffer(params.limit);
    } catch (e) {
      console.error(e);
      setHasError(true);
      return {response: [], offsetsData: {}};
    }

    const {response, remainingBuffer} = takeStockFavoritesProbePage(buffer.current, params.limit);
    buffer.current = remainingBuffer;
    if (response.length === 0 && !hasServedItems.current) {
      setIsEmpty(true);
    }
    hasServedItems.current = hasServedItems.current || response.length > 0;

    return {response, offsetsData: {}};
  };

  const onRetry = (): void => {
    resetPaging();
    dispatch(
      reloadGrid({
        gridId: props.gridID,
        gridSelectionGroupId: props.selectionGroupID,
      })
    );
    setHasError(false);
  };

  if (hasError) {
    return (
      <div className="spacing-m-t-6 _full-width _full-height">
        <StockPanelError text={window.i18next.t('pmwjs_refresh_on_error')} onRefresh={onRetry} />
      </div>
    );
  }

  if (isEmpty) {
    return props.emptyState;
  }

  return (
    <LazyMasonryGrid
      gridId={props.gridID}
      getItems={getItems}
      customOnGridItemClicked={props.onGridItemClicked}
      gridSelectionGroupId={props.selectionGroupID}
      maxSelectionNumber={props.maxSelectionNumber}
      clearGridOnUnmount
      customDimensions={props.gridItemDimensions}
      isItemDraggable={props.enableDragToCanvas}
      onItemPointerDown={props.onItemPointerDown}
      getTopRightContentForGridItem={props.getTopRightContentForGridItem}
      transformResponse={dropTilesAlreadyShown}
      initialItemsBatchLoaded={props.onInitialBatchLoaded}
      makeContentScrollable={false}
      scrollingContainer={props.scrollingContainer}
      doCheckForNextBatchImmediately={false}
      defaultHeight={STOCK_GRID_ITEM_DIMENSION}
    />
  );
}
